/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { LoggerStore } from "../../logger/logger-store"
import type { Result } from "../../network/http-client"
import { makeGetRequest } from "../../network/http-client"

import type { ApplicationDetailStore } from "./application-detail-store"
import type {
    ApplicationApk,
    ApplicationDetail,
    ApplicationPageEvent,
    ApplicationPageFlag
} from "./application-detail-types"

enum Endpoint {
    applicationDetail = "/api/private/application/:applicationId",
    applicationEvents = "/api/private/application/:applicationId/event",
    applicationFlagHistory = "/api/private/application/:applicationId/flag-history",
    applicationMalware = "/api/private/application/av-user-id/:avUserId/malware"
}

export class ApplicationDetailService {
    private readonly appIdPlaceholder = ":applicationId"
    private readonly avUserIdPlaceholder = ":avUserId"
    private readonly PAGE_SIZE = 50

    constructor(
        private readonly store: ApplicationDetailStore,
        private readonly logger: LoggerStore
    ) {}

    async getApplicationDetail(appId: number): Promise<Result<ApplicationDetail>> {
        const detail = await makeGetRequest<ApplicationDetail>(
            Endpoint.applicationDetail.replace(this.appIdPlaceholder, `${appId}`),
            {}
        )
        if (detail.success && detail.data) {
            this.store.setDetail(detail.data)
            return detail
        }
        if (!detail.success) {
            this.logger.logApiError("Get application detail failed", detail.apiError, detail.error)
        }
        return detail
    }

    async getApplicationFlagHistory(appId: number, pageNum: number): Promise<Result<ApplicationPageFlag>> {
        const params = { page: `${pageNum}`, size: `${this.PAGE_SIZE}` }
        const history = await makeGetRequest<ApplicationPageFlag>(
            Endpoint.applicationFlagHistory.replace(this.appIdPlaceholder, `${appId}`),
            params
        )
        if (history.success && history.data) {
            this.store.setFlags(pageNum, history.data)
            return history
        }
        if (!history.success) {
            this.logger.logApiError("Get application flag history failed", history.apiError, history.error)
        }
        return history
    }

    async getApplicationEvents(appId: number, pageNum: number): Promise<Result<ApplicationPageEvent>> {
        const params = { page: `${pageNum}`, size: `${this.PAGE_SIZE}` }
        const events = await makeGetRequest<ApplicationPageEvent>(
            Endpoint.applicationEvents.replace(this.appIdPlaceholder, `${appId}`),
            params
        )
        if (events.success && events.data) {
            this.store.setEvents(pageNum, events.data)
            return events
        }
        if (!events.success) {
            this.logger.logApiError("Get application events failed", events.apiError, events.error)
        }
        return events
    }

    async getApplicationApks(avUserId: string): Promise<Result<ApplicationApk[]>> {
        // Fetch both active and removed APKs; the store separates them.
        const apks = await makeGetRequest<ApplicationApk[]>(
            Endpoint.applicationMalware.replace(this.avUserIdPlaceholder, avUserId),
            { removed: "true" }
        )
        if (apks.success) {
            // Treat an empty response body as an empty list.
            this.store.setApks(apks.data ?? [])
        } else {
            this.logger.logApiError("Get application APKs failed", apks.apiError, apks.error)
        }
        return apks
    }
}
